import { initializeApp } from 'firebase/app';
import {
    getAuth,
    onAuthStateChanged,
    signInWithPopup,
    GoogleAuthProvider,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    signInAnonymously
} from 'firebase/auth';
import {
    getFirestore,
    initializeFirestore,
    memoryLocalCache,
    persistentLocalCache,
    persistentMultipleTabManager,
    doc,
    setDoc,
    getDoc,
    collection,
    addDoc,
    query,
    where,
    onSnapshot,
    orderBy,
    limit,
    startAfter,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    getDocs,
    getCountFromServer,
    getAggregateFromServer,
    sum,
    average,
    writeBatch,
    runTransaction,
    increment,
    documentId,
    arrayUnion,
    arrayRemove
} from 'firebase/firestore';
import {
    getStorage,
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from "firebase/storage";
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { placeholderImages } from '../utils/placeholderUtils';

// --- Firebase Initialization ---
// This code now reads from your .env file
const firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID
};

// Validate config
const validateConfig = () => {
    const required = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
    const missing = required.filter(key => !firebaseConfig[key]);

    if (missing.length > 0) {
        console.error('Missing Firebase config:', missing);
        return false;
    }
    return true;
};

// Every browser on iPhone/iPad runs on WebKit, including Chrome and the LINE/Facebook in-app
// browsers. iPadOS sends a Mac UA, so touch support is what separates it from a real Mac.
// Desktop Safari is WebKit too, so it goes in the same bucket.
function isAppleWebKit() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/.test(ua)) return true;
    if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true;
    return /Safari\//.test(ua) && !/Chrome|Chromium|Edg|OPR|Android/.test(ua);
}

let db, auth, storage, messaging, functions, app, isFirebaseReady = false;

try {
    if (!validateConfig()) {
        throw new Error('Invalid Firebase configuration');
    }

    app = initializeApp(firebaseConfig);

    // Firestore with IndexedDB offline persistence (multi-tab). Suspended tabs (>30 min)
    // that wake up re-attach their listeners from the local cache and only bill the
    // delta, instead of re-billing the FULL snapshot of every listener. Must be the
    // FIRST Firestore init for this app (initializeFirestore throws after getFirestore).
    //
    // Apple WebKit is the exception and gets the in-memory cache. WebKit drops a page's
    // IndexedDB connection while the page sits in the background, and the payment step
    // guarantees that: the buyer leaves for the banking app, then comes back to attach
    // the slip. After that, every write fails LOCALLY with code `unavailable` ("Failed to
    // persist write") before anything is sent to the server, and keeps failing until the
    // page reloads. So BookingModal's order setDoc rejected and a buyer who had already
    // paid got "เกิดข้อผิดพลาดในการสร้างออเดอร์ (รหัส: unavailable)". The catch below
    // can't cover this because the connection dies mid-session, long after init worked.
    // Memory has no IndexedDB to lose. It is also what every browser ran before this cache.
    try {
        db = initializeFirestore(app, {
            localCache: isAppleWebKit()
                ? memoryLocalCache()
                : persistentLocalCache({ tabManager: persistentMultipleTabManager() })
        });
    } catch (persistenceError) {
        // Unsupported browser / private mode / already-initialized conflict —
        // fall back to the default in-memory cache (previous behavior).
        console.warn('⚠️ Firestore offline persistence unavailable, using default cache:', persistenceError);
        db = getFirestore(app);
    }
    auth = getAuth(app);
    storage = getStorage(app);
    functions = getFunctions(app, 'asia-southeast1');

    // App Check (bot/abuse protection). Initializes ONLY when a reCAPTCHA v3 site key is
    // configured, so builds/dev without the key are unaffected. Server-side enforcement on
    // callables is enabled SEPARATELY after the key is registered in the Firebase console.
    try {
        const appCheckKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        if (appCheckKey) {
            if (process.env.REACT_APP_APPCHECK_DEBUG === 'true' && typeof window !== 'undefined') {
                window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;   // localhost/dev debug token
            }
            initializeAppCheck(app, {
                provider: new ReCaptchaV3Provider(appCheckKey),
                isTokenAutoRefreshEnabled: true,
            });
            console.log('✅ App Check initialized');
        } else {
            console.warn('⚠️ App Check not initialized (set REACT_APP_RECAPTCHA_SITE_KEY to enable)');
        }
    } catch (acErr) {
        console.warn('App Check init failed:', acErr);
    }

    isFirebaseReady = true;

    // Initialize FCM (async, non-blocking)
    isSupported().then((supported) => {
        if (supported) {
            messaging = getMessaging(app);
            console.log('✅ FCM initialized');
        } else {
            console.warn('⚠️ FCM not supported in this browser');
        }
    }).catch((err) => console.warn('FCM support check failed:', err));

    console.log('✅ Firebase initialized successfully');
} catch (error) {
    console.error("❌ Firebase initialization failed:", error);
    isFirebaseReady = false;
}

export {
    db,
    auth,
    storage,
    messaging,
    functions,
    httpsCallable,
    isFirebaseReady,
    getToken,
    onMessage,
    isSupported,
    onAuthStateChanged,
    signInWithPopup,
    GoogleAuthProvider,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    signInAnonymously,
    doc,
    setDoc,
    getDoc,
    collection,
    addDoc,
    query,
    where,
    onSnapshot,
    orderBy,
    limit,
    startAfter,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    getDocs,
    getCountFromServer,
    getAggregateFromServer,
    sum,
    average,
    writeBatch,
    runTransaction,
    increment,
    documentId,
    arrayUnion,
    arrayRemove,
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
};

/**
 * Creates a user profile document in Firestore if one doesn't already exist.
 * @param {object} userAuth - The user object from Firebase Authentication.
 * @param {object} additionalData - Any additional data to store for the user.
 */
export const createUserProfileDocument = async (userAuth, additionalData = {}) => {
    if (!userAuth) return;

    try {
        const userDocRef = doc(db, 'users', userAuth.uid);

        // Idempotent: AuthModal and App's users/{uid} snapshot can both call this
        // concurrently for the same brand-new user. A plain get-then-set race would run
        // the profile-create + totalUsers increment twice, double-counting stats.totalUsers.
        // A transaction re-reads the user doc and only creates/increments when it truly
        // doesn't exist, so a concurrent second call is a no-op.
        await runTransaction(db, async (transaction) => {
            const userSnapshot = await transaction.get(userDocRef);
            if (userSnapshot.exists()) {
                // If document exists, we might want to ensure email is up to date or verify existence for calling code
                console.log('User profile already exists');
                return;
            }

            // All reads must precede all writes inside a transaction, so read stats first.
            const statsRef = doc(db, 'public/data/stats', 'website');
            const statsDoc = await transaction.get(statsRef);

            console.log('Creating new user profile for:', userAuth.email);
            const { displayName, email, photoURL } = userAuth;
            const createdAt = serverTimestamp();
            // Use hardcoded placeholder as fallback to be 100% safe
            const defaultPhotoURL = placeholderImages?.noAvatar || 'https://via.placeholder.com/150';

            const newProfile = {
                displayName: displayName || (email ? email.split('@')[0] : 'User'),
                email: email || '',
                photoURL: photoURL || null, // Optional
                createdAt,
                role: 'user', // Default role
                ...additionalData,
            };

            transaction.set(userDocRef, newProfile);

            // Increment aggregated user count in stats document (same transaction, so it
            // only ever runs once per real new user).
            if (statsDoc.exists()) {
                transaction.update(statsRef, {
                    totalUsers: increment(1)
                });
            } else {
                transaction.set(statsRef, {
                    totalUsers: 1,
                    visitors: 0
                }, { merge: true });
            }

            console.log('User profile created successfully');
        });

        return userDocRef;
    } catch (error) {
        console.error("Error creating user profile:", error);
        throw error; // Re-throw to handle in UI
    }
};
